import { Shield, Users, Target, Heart, Lock, Zap, Globe, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function AboutUs() {
  const teamValues = [
    {
      icon: Shield,
      title: "Privacy First",
      description: "We believe privacy is a fundamental human right, not a luxury. Every feature we build starts with this principle."
    },
    {
      icon: Lock,
      title: "Security by Design",
      description: "Your data is encrypted at rest and in transit. We never sell your information - ever."
    },
    {
      icon: Users,
      title: "User Empowerment",
      description: "We don't just protect you - we educate you. Knowledge is the best defense against surveillance."
    },
    {
      icon: Heart,
      title: "Transparency",
      description: "No hidden fees, no confusing terms. We're upfront about what we do and how we do it."
    }
  ];

  const stats = [
    { value: "10M+", label: "Breaches Monitored" },
    { value: "14+", label: "Data Brokers Tracked" },
    { value: "24/7", label: "Threat Monitoring" },
    { value: "99.9%", label: "Uptime" }
  ];

  return (
    <div className="min-h-screen bg-[#050505] py-16" data-testid="about-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-6">
            <Shield className="w-10 h-10 text-[#00FF94]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            About <span className="text-[#00FF94]">PrivacyShield</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            We're on a mission to give everyone the tools to take back control of their digital privacy.
          </p>
        </div>

        {/* Our Story */}
        <div className="mb-20">
          <Card className="bg-[#0a0a0a] border-[#262626] overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-2xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  PrivacyShield was born from a simple realization: <span className="text-white">your personal data is being bought and sold without your knowledge or consent.</span>
                </p>
                <p>
                  In 2023, we discovered that government agencies were using tools like "Fog Reveal" to purchase location data from apps on millions of phones - all without a warrant. Data brokers were compiling detailed profiles on everyday people, selling everything from your home address to your daily routines.
                </p>
                <p>
                  We knew there had to be a better way. So we built PrivacyShield - a comprehensive platform that doesn't just tell you about threats, but gives you the tools to fight back. From checking if your passwords have been compromised to removing your data from broker databases, we handle the heavy lifting so you can live your digital life with confidence.
                </p>
                <p className="text-[#00FF94] font-medium">
                  Today, we're proud to help thousands of users protect their digital identity every day.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6 text-center"
            >
              <p className="text-3xl md:text-4xl font-bold text-[#00FF94] mb-2">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Our Values */}
        <div className="mb-20">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {teamValues.map((value, index) => (
              <Card key={index} className="bg-[#0a0a0a] border-[#262626]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-6 h-6 text-[#00FF94]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                      <p className="text-gray-500 text-sm">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-20">
          <Card className="bg-gradient-to-br from-[#00FF94]/5 to-transparent border-[#00FF94]/20">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-2xl font-bold text-white mb-6">What Makes Us Different</h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <Zap className="w-8 h-8 text-[#00FF94] mb-3" />
                  <h3 className="text-white font-semibold mb-2">AI-Powered Protection</h3>
                  <p className="text-gray-500 text-sm">
                    Our AI assistant Alek provides personalized privacy advice 24/7, helping you make informed decisions.
                  </p>
                </div>
                <div>
                  <Globe className="w-8 h-8 text-[#00FF94] mb-3" />
                  <h3 className="text-white font-semibold mb-2">Real Breach Data</h3>
                  <p className="text-gray-500 text-sm">
                    We use the Have I Been Pwned database to check your passwords against billions of real breach records.
                  </p>
                </div>
                <div>
                  <Award className="w-8 h-8 text-[#00FF94] mb-3" />
                  <h3 className="text-white font-semibold mb-2">Transparent Pricing</h3>
                  <p className="text-gray-500 text-sm">
                    No hidden fees, no upsells. What you see is what you get - premium protection at a fair price.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Have Questions?</h2>
          <p className="text-gray-400 mb-6">
            Our AI assistant Alek is available 24/7 to help you with any privacy concerns.
          </p>
          <a 
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#00FF94] text-black font-semibold rounded-lg hover:bg-[#00FF94]/90 transition-colors"
          >
            <Users className="w-5 h-5" />
            Contact Us
          </a>
        </div>

      </div>
    </div>
  );
}
